import { faMeta } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpRightFromSquare, faRectangleAd } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SimpleGrid } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IFacebookPage, OmniCampaign } from '@feathr/blackbox';
import { Button, CardV2 as Card, SelectableCard, UniversalSelect } from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';

import { useChannelsCard } from './ChannelsCard.hooks';

interface IChannelsCardProps {
  campaign: OmniCampaign;
}

function ChannelsCard({ campaign }: Readonly<IChannelsCardProps>): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const {
    displaySelected,
    metaSelected,
    handleDisplayChange,
    handleMetaChange,
    metaCardState,
    metaLockedTooltip,
    facebookPages,
    isLoadingFacebookPages,
    selectedFacebookPage,
    handleFacebookPageSelect,
  } = useChannelsCard(campaign);

  const isMetaLocked = metaCardState === 'locked';
  const isMetaSetup = metaCardState === 'setup';

  function getOptionLabel(page: IFacebookPage): string {
    return page.name;
  }

  function getOptionValue(page: IFacebookPage): string {
    return page.id;
  }

  const setupButton = (
    <Button
      href={localUrl('settings/integrations/meta')}
      name={'meta-setup'}
      suffix={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
      target={'_blank'}
      type={'secondary'}
    >
      {t('Set up')}
    </Button>
  );

  return (
    <Card>
      <Card.Header
        description={t(
          'Choose the platforms where you want your Pixl+ campaign to run. You can select one or both channels.',
        )}
        title={t('Channels')}
      />
      <Card.Content addVerticalGap={true}>
        <SimpleGrid cols={2}>
          <SelectableCard
            checked={displaySelected}
            contentOrientation={'vertical'}
            data-name={'channel-display'}
            description={t(
              'Reach your audience through display ads on websites across the internet',
            )}
            name={'channel-display'}
            onChange={handleDisplayChange}
            prefix={<FontAwesomeIcon icon={faRectangleAd} />}
            theme={'cyan'}
            title={t('Display Advertising')}
          />
          <SelectableCard
            checked={metaSelected}
            contentOrientation={'vertical'}
            data-name={'channel-meta'}
            description={
              isMetaSetup
                ? t(
                    'Integrate with Meta in order to advertise on Facebook and Instagram. Once connected, return here to finish setting up.',
                  )
                : t('Connect with users on Facebook, Instagram, and other Meta platforms')
            }
            disabled={isMetaSetup}
            locked={isMetaLocked}
            name={'channel-meta'}
            onChange={handleMetaChange}
            prefix={<FontAwesomeIcon icon={faMeta} />}
            suffix={isMetaSetup ? setupButton : undefined}
            theme={'cyan'}
            title={t('Meta Advertising')}
            tooltip={metaLockedTooltip}
          />
        </SimpleGrid>
        {metaSelected && !isMetaSetup && (
          <UniversalSelect<IFacebookPage>
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            helpText={t('Select which Facebook page this campaign will be served from.')}
            isLoading={isLoadingFacebookPages}
            label={t('Facebook page')}
            name={'facebook-page-select'}
            onSelectSingle={handleFacebookPageSelect}
            options={facebookPages}
            placeholder={t('Select a Facebook page')}
            required={true}
            value={selectedFacebookPage}
          />
        )}
      </Card.Content>
    </Card>
  );
}

export default observer(ChannelsCard);
